import axios from 'axios';
import type { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getMockBaseUrl, handleMockRequest } from '../utils/mock';
import { login } from '../store/modules/authSlice';

// 定义 Shopify 实例的接口
interface ShopifyInstance {
  idToken: () => Promise<string>;
  config: { shop: string };
}

// 创建一个全局存储的 shopify 实例变量
// 使用更宽松的类型定义，确保可以访问所需属性
let shopifyInstance: ShopifyInstance | null = null;

// 设置 shopify 实例的函数
export const setShopifyInstance = (instance: unknown) => {
  // 类型检查确保实例有所需方法
  if (instance && 
      typeof instance === 'object' && 
      'idToken' in instance && 
      typeof (instance as Record<string, unknown>).idToken === 'function' &&
      'config' in instance &&
      typeof (instance as Record<string, unknown>).config === 'object' &&
      'shop' in ((instance as Record<string, unknown>).config as Record<string, unknown>)) {
    shopifyInstance = instance as ShopifyInstance;
  } else {
    console.error('Invalid Shopify instance passed to setShopifyInstance');
  }
};

// 获取 shopify 实例的函数
const getShopify = () => {
  if (!shopifyInstance) {
    console.warn('Shopify instance not set. Some features may not work properly.');
  }
  return shopifyInstance;
};

// 创建一个翻译帮助函数
const getTranslationFunction = () => {
  // 如果在服务器端或初始化阶段，提供一个基本的翻译函数
  return (key: string, options?: Record<string, unknown>) => {
    // 基本的错误消息映射
    const errorMessages: Record<string, string> = {
      'error.unknownError': 'Unknown error',
      'error.badRequest': 'Bad request parameters',
      'error.unauthorized': 'Unauthorized or login expired',
      'error.forbidden': 'Access denied',
      'error.notFound': 'Requested resource not found',
      'error.serverError': 'Server internal error',
      'error.httpError': `Request failed, HTTP status: ${options?.status || 'unknown'}`,
      'error.networkError': 'Network error, please check your connection',
      'error.requestError': 'Request configuration error',
      'error.maxRetries': `Request failed after ${options?.maxRetries || 5} retries: ${options?.message || ''}`
    };
    
    // 返回映射的消息或键本身
    return errorMessages[key] || key;
  };
};

/**
 * 获取Shopify IdToken
 * @returns {Promise<string>} 返回Shopify IdToken
 */
const shopifyToken = async (): Promise<string> => {
  const shopify = getShopify();
  if (!shopify) {
    throw new Error('Shopify instance not available');
  }
  return await shopify.idToken();
};

// 延迟加载store和actions，避免循环依赖
const getStore = async () => {
  const storeModule = await import('../store');
  return storeModule.default;
};

const getStoreState = async () => {
  const store = await getStore();
  return store.getState();
};

const getAuthActions = async () => {
  const authModule = await import('../store/modules/authSlice');
  return {
    setWilllinksToken: authModule.setWilllinksToken
  };
};

// 添加标志变量防止重复调用
let isAuthenticating = false;

// 添加获取buserAuth接口的函数
const getBuserAuth = async (): Promise<string | null> => {
  // 如果已经在认证过程中，则直接返回null避免循环
  if (isAuthenticating) {
    console.log('Authentication already in progress, skipping recursive call');
    return null;
  }
  
  try {
    // 设置认证标志，防止重复调用
    isAuthenticating = true;
    
    // 使用auth.ts中的login函数调用buserAuth API，传入shopDomain
    const shopify = getShopify();
    if (!shopify) {
      throw new Error('Shopify instance not available');
    }
    
    // 从store中获取dispatch函数
    const store = await getStore();
    // 调用login action并等待结果
    const resultAction = await store.dispatch(login({ shopDomain: shopify.config.shop }));
    // 检查action是否成功
    if (login.fulfilled.match(resultAction)) {
      const response = resultAction.payload;
      if (response?.token) {
        const willlinksToken = response.token;
        
        // 更新Redux中的Token
        const authActions = await getAuthActions();
        store.dispatch(authActions.setWilllinksToken(willlinksToken));
        
        // 输出完整信息，包括token, brandId, expiresAt
        console.log('Successfully obtained auth info:', {
          token: response.token,
          brandId: response.brandId,
          expiresAt: response.expiresAt
        });
        
        return willlinksToken;
      }
    }
    
    console.log('No willlinksToken in buserAuth response');
    return null;
  } catch (error) {
    console.error('Failed to get willlinksToken from buserAuth:', error);
    return null;
  } finally {
    // 重置认证标志
    isAuthenticating = false;
  }
};

// 扩展 InternalAxiosRequestConfig 类型，添加 _retry 属性
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

/**
 * 请求队列管理
 */
// 队列状态：空闲(IDLE)、刷新中(REFRESHING)
type QueueState = 'IDLE' | 'REFRESHING';
// 队列中的请求对象类型
interface QueuedRequest {
  config: InternalAxiosRequestConfig;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

// 请求队列
let requestQueue: QueuedRequest[] = [];
// 队列状态
let queueState: QueueState = 'IDLE';
// 是否正在刷新Token
let isRefreshingToken = false;

/**
 * 刷新WilllinksToken
 * @returns Promise<string> 返回新的Token
 */
const refreshWilllinksToken = async (): Promise<string> => {
  try {
    // 标记正在刷新Token
    isRefreshingToken = true;
    
    // 使用buserAuth接口获取新的WilllinksToken
    const newToken = await getBuserAuth();
    if (!newToken) {
      throw new Error('Failed to get willlinksToken from buserAuth');
    }
    
    console.log('Successfully refreshed WilllinksToken');
    return newToken;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    // 如果刷新失败，可能需要重新登录
    const store = await getStore();
    store.dispatch({ type: 'auth/logout' });
    throw new Error('Failed to refresh token, please login again');
  } finally {
    isRefreshingToken = false;
  }
};

/**
 * 添加请求到队列中
 * @param request 请求对象
 */
const addToQueue = (request: QueuedRequest): void => {
  requestQueue.push(request);
  
  // 如果队列状态为空闲，则开始处理队列
  if (queueState === 'IDLE') {
    processQueue();
  }
};

/**
 * 处理队列中的请求
 */
const processQueue = async (): Promise<void> => {
  // 如果队列为空，将状态设为空闲并返回
  if (requestQueue.length === 0) {
    queueState = 'IDLE';
    return;
  }
  
  // 设置队列状态为刷新中
  queueState = 'REFRESHING';
  
  try {
    // 如果未在刷新Token，则刷新Token
    if (!isRefreshingToken) {
      await refreshWilllinksToken();
    }
    
    // 获取最新的Token
    const state = await getStoreState();
    const willlinksToken = state.auth?.willlinksToken || '';
    
    // 执行队列中的所有请求
    const requests = [...requestQueue];
    requestQueue = [];
    
    // 为每个请求添加最新的Token
    for (const request of requests) {
      const config = { ...request.config };
      if (willlinksToken) {
        config.headers['X-Willlinks-Token'] = willlinksToken;
      }
      
      try {
        // 重新发起请求
        const response = await axios(config);
        request.resolve(response);
      } catch (error) {
        request.reject(error);
      }
    }
    
    // 处理完成，将状态设为空闲
    queueState = 'IDLE';
  } catch (error) {
    // 如果刷新Token失败，拒绝所有队列中的请求
    const failedRequests = [...requestQueue];
    requestQueue = [];
    
    for (const request of failedRequests) {
      request.reject(new Error('Failed to refresh token'));
    }
    
    queueState = 'IDLE';
    console.error('Failed to process queue:', error);
  }
};

/**
 * API响应接口
 * 定义通用的API响应格式
 */
export interface ApiResponse<T = unknown> {
  /** 状态码，0表示成功，非0表示失败 */
  code: number;
  /** 业务状态码，用于更详细的错误描述 */
  reasonCode?: number;
  /** 响应消息 */
  message: string;
  /** 响应消息 */
  msg: string;
  /** 响应数据 */
  data: T;
}

/**
 * 自定义API错误类
 */
export class ApiError extends Error {
  /** API状态码 */
  code: number;
  /** 业务状态码 */
  reasonCode?: number;
  /** 原始错误对象 */
  originalError?: unknown;

  constructor(message: string, code: number, reasonCode?: number, originalError?: unknown) {
    super(message);
    this.name = '';
    this.code = code;
    this.reasonCode = reasonCode;
    this.originalError = originalError;
  }
}

/**
 * 创建 axios 实例
 * 可以在这里配置基础URL、超时时间、请求头等全局配置
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: `${import.meta.env.PUBLIC_API_URL}`,
  timeout: 10000, // 请求超时时间：10秒
  // 支持跨域携带cookie
  withCredentials: false
});

/**
 * 请求拦截器
 * 在发送请求之前执行，可以在此处理请求配置
 * 例如：添加token、处理请求参数等
 */
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // 从Redux store中获取认证信息
      const state = await getStoreState();
      let willlinksToken = state.auth?.willlinksToken || ''; // 获取willlinksToken
      const token = state.auth?.token || ''; // 获取普通token

      config.headers['Content-Type'] = 'application/json';
      config.headers['X-Willlinks-AppType'] = '1';
      config.headers['X-Willlinks-FromType'] = '1';

      // 获取Shopify idToken
      let idToken = '';
      
      try {
        // 获取Shopify Token
        idToken = await shopifyToken();
        console.log('Successfully obtained Shopify idToken for request');
        
        // 确定请求的URL是否是认证相关的路径，如果是则不进行token获取，避免循环
        const url = config.url || '';
        const isAuthRelatedRequest = url.includes('/system/buserAuth') || 
                                     url.includes('/installShopifyShop') || 
                                     url.includes('/api/brand/v1/system/buserAuth') ||
                                     url.includes('/api/brand/v1/installShopifyShop');
        
        // 如果没有willlinksToken，有idToken，且不是认证相关的请求，则尝试获取token
        if (!willlinksToken && !isAuthRelatedRequest && !isAuthenticating) {
          const newWilllinksToken = await getBuserAuth();
          if (newWilllinksToken) {
            willlinksToken = newWilllinksToken;
          }
        }
      } catch (error) {
        console.error('Failed to get Shopify idToken:', error);
        // 继续请求流程，即使没有获取到 Shopify 令牌
      }

      // 添加Authorization头(优先使用token)
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else if (idToken) {
        config.headers.Authorization = `Bearer ${idToken}`;
      }
      
      // 添加X-Willlinks-Token头
      if (willlinksToken) {
        config.headers['X-Willlinks-Token'] = willlinksToken;
      }

      // 处理 mock 数据
      const mockedConfig = handleMockRequest(config);
      if (mockedConfig) {
        return mockedConfig;
      }
      
      return config;
    } catch (error) {
      console.error('Error in request interceptor:', error);
      return config;
    }
  },
  (error) => {
    // 请求错误处理
    console.error('Error in request:', error);
    return Promise.reject(error);
  }
);

/**
 * 处理API业务逻辑错误
 * @param response API响应数据
 */
const handleApiError = <T>(response: ApiResponse<T>): T => {
  // 如果状态码不为0，则表示API调用失败
  if (response.code !== 0) {
    let errorMessage = response.msg || 'Request failed';
    
    // 根据业务状态码(reasonCode)判断具体错误类型并给出更明确的错误信息
    if (response.reasonCode) {
      switch (response.reasonCode) {
        case 1001:
          errorMessage = '用户未登录或登录已过期';
          // Token 过期错误在响应拦截器中处理，这里不做处理
          // 我们让错误继续传播，以便在响应拦截器中捕获
          throw new ApiError(errorMessage, response.code, response.reasonCode);
        case 1002:
          errorMessage = '权限不足，无法访问该资源';
          break;
        case 2001:
          errorMessage = '请求参数错误';
          break;
        case 3001:
          errorMessage = '数据不存在';
          break;
        case 3002:
          errorMessage = '操作失败，数据已被修改';
          break;
        case 4001:
          errorMessage = '系统内部错误';
          break;
        case 5001:
          errorMessage = '第三方服务调用失败';
          break;
        // 可以根据实际业务需求添加更多的状态码处理
        default:
          errorMessage = `${errorMessage}`;
      }
    }
    
    // 抛出API业务错误
    throw new ApiError(errorMessage, response.code, response.reasonCode);
  }
  
  // 返回成功的业务数据
  return response.data;
};

/**
 * 处理HTTP错误
 * @param error AxiosError对象
 * @returns 格式化的错误信息
 */
const handleHttpError = (error: AxiosError): string => {
  // 获取翻译函数
  const t = getTranslationFunction();
  
  // 服务器返回了错误状态码
  const status = error.response?.status;
  let errorMessage = t('error.unknownError');
  
  // 根据HTTP状态码给出友好的错误提示
  switch (status) {
    case 400:
      errorMessage = t('error.badRequest');
      break;
    case 401:
      errorMessage = t('error.unauthorized');
      break;
    case 403:
      errorMessage = t('error.forbidden');
      break;
    case 404:
      errorMessage = t('error.notFound');
      break;
    case 500:
      errorMessage = t('error.serverError');
      break;
    default:
      errorMessage = t('error.httpError', { status });
  }
  
  return errorMessage;
};

/**
 * 响应拦截器
 * 在接收到响应后执行，可以在此处理响应数据
 * 例如：统一处理错误、格式化响应数据等
 */
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // 处理API响应
    const data = response.data;
    
    // 检查响应数据是否符合API响应格式
    if (data && 'code' in data) {
      try {
        // 处理API业务逻辑错误
        return handleApiError(data);
      } catch (error) {
        // 检查是否是Token过期错误
        if (error instanceof ApiError && error.reasonCode === 1001) {
          const originalRequest = response.config;
          
          // 确保不会无限重试
          if (!originalRequest._retry) {
            // 标记该请求已经重试过
            originalRequest._retry = true;
            
            // 创建一个Promise，将请求添加到队列
            return new Promise<unknown>((resolve, reject) => {
              console.log('Adding request to queue due to token expiration');
              addToQueue({
                config: originalRequest,
                resolve,
                reject
              });
            });
          }
        }
        
        // 对于其他错误或已重试过的请求，继续抛出错误
        throw error;
      }
    }
    
    // 如果响应数据不符合标准API格式，则直接返回
    return data;
  },
  async (error) => {
    // 获取翻译函数
    const t = getTranslationFunction();
    
    // 创建原始请求的配置
    const originalRequest = error.config;
    
    // 检查是否是Token过期错误 (reasonCode === 1001)
    // 可能来自响应体或响应拦截器抛出的ApiError
    if (
      (error.response?.data?.reasonCode === 1001 || 
      (error instanceof ApiError && error.reasonCode === 1001)) && 
      !originalRequest?._retry
    ) {
      // 标记该请求已经重试过，防止无限循环
      originalRequest._retry = true;
      
      // 创建一个Promise，将请求添加到队列
      return new Promise<unknown>((resolve, reject) => {
        console.log('Adding request to queue due to token expiration (error handler)');
        addToQueue({
          config: originalRequest,
          resolve,
          reject
        });
      });
    }
    
    // 处理HTTP错误
    if (error.response) {
      // 服务器返回了错误状态码
      const status = error.response.status;
      const errorMessage = handleHttpError(error);
      
      // 创建并抛出API错误
      throw new ApiError(
        errorMessage, 
        status, 
        undefined, 
        error
      );
    }
    
    if (error.request) {
      // 请求已发送但未收到响应
      throw new ApiError(
        t('error.networkError'), 
        -1, 
        undefined, 
        error
      );
    }
    
    // 请求配置错误
    throw new ApiError(
      error.message || t('error.requestError'), 
      -2, 
      undefined, 
      error
    );
  }
);

/**
 * 请求选项接口
 * 定义请求所需的参数类型
 */
interface RequestOptions {
  /** API请求路径 */
  url: string;
  /** 请求方法，默认为GET */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** 请求体数据，用于POST/PUT等请求 */
  data?: Record<string, unknown>;
  /** URL查询参数，会被添加到URL后面 */
  params?: Record<string, unknown>;
  /** 最大重试次数，默认为5次 */
  maxRetries?: number;
}

/**
 * axios请求封装
 * 提供统一的请求方法，并实现了网络错误自动重试功能
 */
const axiosWrapper = {
  /**
   * 发送请求方法
   * @param options 请求选项
   * @returns Promise 返回请求结果
   */
  request: async <T = unknown>({ url, method = 'GET', data, params, maxRetries = 5 }: RequestOptions): Promise<T> => {
    // 获取翻译函数
    const t = getTranslationFunction();
    
    // 重试计数器
    let retries = 0;
    
    /**
     * 执行请求的内部函数
     * 支持递归调用实现请求重试
     */
    const executeRequest = async (): Promise<T> => {
      try {
        console.log(`发起${method}请求: ${url}`); // 请求开始的日志标记
        
        // 发送请求
        const response = await axiosInstance({
          url,
          method,
          data,
          params,
        });
        return response as T;
      } catch (error: unknown) {
        // 判断是否为API错误
        if (error instanceof ApiError) {
          // 将API错误直接向上传递
          console.error(`API错误 (${error.code})`, error.message);
          throw error;
        }
        
        // 将错误转换为AxiosError类型以便访问其属性
        const axiosError = error as AxiosError;
        
        // 判断是否为网络错误或超时错误
        const isNetworkError = !axiosError.response || 
          axiosError.code === 'ECONNABORTED' || 
          axiosError.message.includes('Network Error') ||
          axiosError.message.includes('timeout');
        
        // 如果是网络错误且未达到最大重试次数，则重试
        if (isNetworkError && retries < maxRetries) {
          retries++;
          console.log(`请求失败，正在进行第 ${retries} 次重试...`);
          
          // 延迟1秒后重试，避免立即重试导致的连续失败
          await new Promise((resolve) => {
            setTimeout(resolve, 1000);
          });
          return executeRequest(); // 递归调用自身进行重试
        }
        
        // 达到最大重试次数或非网络错误，则抛出错误
        console.error('Request error:', axiosError);
        if (retries >= maxRetries && isNetworkError) {
          throw new ApiError(
            t('error.maxRetries', { maxRetries, message: axiosError.message }),
            -3,
            undefined,
            axiosError
          );
        }
        
        // 将其他错误包装为ApiError
        throw new ApiError(
          axiosError.message || t('error.unknownError'),
          -4,
          undefined,
          axiosError
        );
      }
    };
    
    // 开始执行请求
    return executeRequest();
  },
};

export default axiosWrapper;
